<template>
  <div class="mb-4">
    <!-- System message -->
    <div class="flex text-gray-500 text-sm items-center" v-if="messageData.type === 'system'">
      <div class="mr-2">{{ formatedDate }}</div>
      <div
        class="mr-2 border border-gray-500 rounded-full w-6 h-6 flex items-center justify-center"
      >
        {{ systemMessageNumber(messageData) }}
      </div>
      {{ messageData.content }}
    </div>
    <!-- Participants message -->
    <div class="flex" v-if="messageData.type === 'message'">
      <div
        class="flex w-full items-top mb-3 text-vice-dark-grey gap-2.5"
        :class="{ 'flex-row-reverse': isMe }"
      >
        <vice-avatar
          :image="messageData?.sender?.avatar"
          :name="messageData?.sender?.name"
          class="w-8 h-8"
          style="min-width: 32px"
        ></vice-avatar>
        <div
          class="flex-grow rounded-lg p-2 text-sm text-gray-700 border border-gray-180"
          style="word-break: break-word"
          :class="{ 'bg-vice-light-grey': !isMe, ' bg-vice-easy-gray': isMe }"
        >
          <div>
            <span class="font-bold mr-2">{{ messageData?.sender?.name }}</span>
            {{ formatedDate }}
          </div>
          <p v-html="messageContent(messageData)"></p>
        </div>
      </div>
    </div>

    <!-- Attachment message -->
    <div class="flex text-white" v-if="messageData.type == 'attachment'">
      <div
        class="flex w-full items-top mb-3 text-vice-dark-grey gap-2.5"
        :class="{ 'flex-row-reverse': isMe }"
      >
        <vice-avatar
          class="w-8 h-8"
          style="min-width: 32px"
          :image="messageData?.sender?.avatar"
          :name="messageData?.sender?.name"
        ></vice-avatar>

        <div
          class="flex-grow rounded-lg p-2 text-sm"
          :class="{
            'bg-vice-light-grey': !isMe,
            ' bg-vice-easy-gray text-vice-dark-gray': isMe,
            'flex-grow': messageData.attachment.thumbnail,
          }"
        >
          <div>
            <span class="font-bold mr-2">{{ messageData.sender.name }}</span>
            {{ formatedDate }}
          </div>

          <div class="flex flex-col cursor-pointer">
            <div target="_blank" @click="attachmentPreviewOpen = true">
              <img
                class="w-full rounded overflow-hidden my-2 block"
                :src="messageData.attachment.url"
                alt=""
                v-if="messageData.attachment.thumbnail"
              />
            </div>

            <a
              class="py-2 flex items-center"
              :href="messageData.attachment.url"
              v-if="!messageData.attachment.thumbnail"
              target="_blank"
            >
              <span class="inline-flex"><DocumentDownloadIcon class="w-6 h-6 mr-1" /></span>
              <span class="underline break-all">
                {{ messageData.attachment.fileName }}
              </span>
            </a>
            <p v-html="messageContent(messageData)" v-if="messageData.content"></p>
          </div>
        </div>
      </div>

      <TransitionRoot as="template" :show="attachmentPreviewOpen">
        <Dialog as="div" class="relative z-30" @close="attachmentPreviewOpen = false">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
          </TransitionChild>

          <div class="fixed inset-0 z-10 overflow-y-auto">
            <div
              class="
                flex
                min-h-full
                items-end
                justify-center
                p-4
                text-center
                sm:items-center
                sm:p-0
              "
            >
              <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="
                    relative
                    transform
                    overflow-hidden
                    rounded-lg
                    bg-white
                    px-4
                    pt-5
                    pb-4
                    text-left
                    shadow-xl
                    transition-all
                    max-h-screen
                    sm:my-8
                    sm:max-w-7xl
                    sm:p-6
                  "
                >
                  <img
                    class="w-full max-h-screen rounded overflow-hidden my-2 block object-contain"
                    :src="messageData.attachment.url"
                    alt=""
                  />
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { DocumentDownloadIcon } from '@heroicons/vue/solid';
import linkifyHtml from 'linkify-html';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';

export default {
  components: {
    ViceAvatar,
    DocumentDownloadIcon,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },

  props: {
    messageData: {
      required: true,
    },
    userId: {
      required: true,
    },
    clinicianName: {
      type: String,
    },
  },
  data: () => ({
    systemMessages: {
      introduction: 1,
      consultation: 2,
      debrief: 3,
    },
    attachmentPreviewOpen: false,
  }),
  setup() {
    return { VICE_SESSION_PHASES };
  },
  methods: {
    messageContent(messageData) {
      const options = {
        defaultProtocol: 'https',
        className: 'underline break-all',
        target: '_blank',
      };
      const content = linkifyHtml(messageData.content, options);

      if (
        messageData.sendTo == 'clinician' &&
        messageData.phase.id != VICE_SESSION_PHASES.DEBRIEF
      ) {
        return `<span
        class="text-vice-rose-400 font-semibold text-vice-rose-400 font-semibold bg-white rounded px-1 mr-1">
          @${this.clinicianName}</span>${content}`;
      }
      return content;
    },
    systemMessageNumber(messageData) {
      let msgNumber = 1;
      if (messageData.content.startsWith('Consultation')) {
        msgNumber = 2;
      } else if (messageData.content.startsWith('Debrief')) {
        msgNumber = 3;
      }

      return msgNumber;
    },
  },

  computed: {
    isMe() {
      return this.userId == this.messageData?.sender?.id;
    },
    formatedDate() {
      return dayjs(this.messageData.sentAt).format('HH:mm');
    },
  },
};
</script>

<style></style>
